// Book a visit — a 4-step wizard (reason → doctor → time → confirm).
// Step 01 is Luan's pre-visit INTAKE (intake-forms.html): pick a chief-complaint category, answer a
// short questionnaire. Only the coarse NON-CLINICAL bucket reaches the backend as `label` (D5); the
// granular answers are persisted separately, encrypted + RLS-scoped, to demo_visit_intake so the
// provider can recall them on /patients/:id (see lib/intakeForms.tsx + lib/intakeApi.ts).
// Step 02 opens the rich doctor profile card (THU-358): clicking a doctor previews their card;
// favoriting sorts them to the top; "Choose" selects + advances. Steps 03–04 are unchanged. The slot
// is validated server-side against the doctor's live calendar.
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ApiError } from "@forcebase-ai/api-client";
import { Avatar, Modal } from "@championcare/ui";
import { useApi } from "../api";
import { useAsync } from "../lib/useAsync";
import { useDoctorsApi, type DoctorProfile } from "../lib/doctorsApi";
import { useIntakeApi, type IntakeResponse } from "../lib/intakeApi";
import { formatDateTime } from "../lib/format";
import { Loading, ErrorState } from "../components/ui";
import { DoctorCard } from "../components/DoctorCard";
import { SlotPicker } from "../components/SlotPicker";
import {
  INTAKE_FORMS,
  INTAKE_ICONS,
  VISIT_CATEGORIES,
  CATEGORY_LABEL,
  formsForCategory,
  type VisitCategory,
} from "../lib/intakeForms";

const STEPS = ["Reason", "Doctor", "Time", "Confirm"];

function doctorName(d: { first_name?: string | null; last_name?: string | null }): string {
  const name = [d.first_name, d.last_name].map((s) => (s ?? "").trim()).filter(Boolean).join(" ");
  return name ? `Dr. ${name}` : "";
}

const Arrow = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
);
const Chevron = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
);
// Wrap an intake icon's inner markup (app-authored, static) into a full SVG string.
const svgWrap = (inner: string) =>
  `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">${inner}</svg>`;

export function Book() {
  const api = useApi();
  const navigate = useNavigate();
  const doctorsApi = useDoctorsApi();
  const intakeApi = useIntakeApi();
  const doctors = useAsync(() => doctorsApi.list(), []);

  const [step, setStep] = useState(1);
  // Step 01 intake: the chosen category drives the form shown + the coarse `bucket` (the ONLY value
  // stored as the appointment label, D5). `answers` holds the granular responses (stored separately).
  // Top-level care category (Medical · Dental · Vision). Defaults to Medical so step 01 opens exactly
  // as it did before; switching it filters the reason grid below.
  const [category, setCategory] = useState<VisitCategory>("medical");
  const [formKey, setFormKey] = useState<string | null>(null);
  const form = INTAKE_FORMS.find((f) => f.key === formKey) ?? null;
  const [answers, setAnswers] = useState<Record<string, string | string[]>>({});
  const [doctorId, setDoctorId] = useState<string>("");
  const [slot, setSlot] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // The doctor whose profile card is open in the Step-2 modal (null = closed).
  const [cardDoctor, setCardDoctor] = useState<DoctorProfile | null>(null);

  if (doctors.loading) return <Loading />;
  if (doctors.error) return <ErrorState message={doctors.error} />;
  const docList = doctors.data ?? [];
  const doctor = docList.find((d) => d.id === doctorId);

  const canContinue =
    (step === 1 && !!form) || (step === 2 && !!doctorId) || (step === 3 && !!slot);

  const setSingle = (q: string, v: string) => setAnswers((a) => ({ ...a, [q]: v }));
  const setTextAns = (q: string, v: string) => setAnswers((a) => ({ ...a, [q]: v }));
  const toggleMulti = (q: string, v: string) =>
    setAnswers((a) => {
      const cur = Array.isArray(a[q]) ? (a[q] as string[]) : [];
      return { ...a, [q]: cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v] };
    });

  async function submit() {
    if (!slot || !doctorId) return;
    setBusy(true);
    setErr(null);
    try {
      // D5: send only the coarse non-clinical bucket as `label`.
      const booked = await api.book({ doctor_id: doctorId, scheduled_at: slot, label: form?.bucket });
      // Persist the granular intake answers against the new appointment (best-effort, encrypted).
      if (form && booked?.id) {
        const responses: IntakeResponse[] = form.fields
          .filter((f) => f.t !== "note")
          .map((f) => {
            const v = answers[(f as { q: string }).q];
            const a = Array.isArray(v) ? v.join(", ") : (v ?? "");
            return { q: (f as { q: string }).q, a: a.trim() };
          })
          .filter((r) => r.a);
        await intakeApi.submit(booked.id, { category: form.category, form_key: form.key, form_title: form.title, responses });
      }
      navigate("/visits");
    } catch (e) {
      setErr(
        e instanceof ApiError && e.code === "slot_unavailable"
          ? "That time was just taken — please pick another."
          : e instanceof ApiError
            ? e.message
            : "Could not book the visit.",
      );
      setStep(3);
      setSlot(null);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="app-page-head">
        {step === 1 && (
          <>
            <h1>What&rsquo;s <em>going on?</em></h1>
            <p>A few quick questions so your doctor walks in already knowing your situation.</p>
          </>
        )}
        {step === 2 && (
          <>
            <h1>Pick your <em>doctor.</em></h1>
            <p>Every physician is board-certified. Choose whose specialty fits — switch anytime.</p>
          </>
        )}
        {step === 3 && (
          <>
            <h1>Pick a day and <em>time.</em></h1>
            <p>Open slots on {doctor ? doctorName(doctor) : "your doctor"}&rsquo;s calendar, in your local time zone.</p>
          </>
        )}
        {step === 4 && (
          <>
            <h1>You&rsquo;re <em>all set.</em></h1>
            <p>Review your visit details and confirm. You&rsquo;ll get a reminder 15 minutes before.</p>
          </>
        )}
      </div>

      <div className="book-steps">
        {STEPS.map((s, i) => {
          const n = i + 1;
          return (
            <div key={s} className={`book-step ${n === step ? "active" : ""} ${n < step ? "done" : ""}`}>
              <span className="book-step-num">0{n}</span>{s}
            </div>
          );
        })}
      </div>

      <div className="book-grid">
        <div>
          {step === 1 && (
            <div className="book-stage active">
              {!form ? (
                <>
                  <div className="intake-cats" role="tablist" aria-label="Care category">
                    {VISIT_CATEGORIES.map((cat) => (
                      <button
                        key={cat.key}
                        type="button"
                        role="tab"
                        aria-selected={category === cat.key}
                        className={`intake-cat ${category === cat.key ? "on" : ""}`}
                        onClick={() => setCategory(cat.key)}
                      >
                        <span className="intake-cat-ic" dangerouslySetInnerHTML={{ __html: svgWrap(INTAKE_ICONS[cat.icon]!) }} />
                        {cat.title}
                      </button>
                    ))}
                  </div>
                  <div className="intake-grid">
                    {formsForCategory(category).map((f) => (
                      <button
                        key={f.key}
                        type="button"
                        className="intake-reason"
                        onClick={() => { setFormKey(f.key); setAnswers({}); }}
                      >
                        <span className="intake-tile" dangerouslySetInnerHTML={{ __html: svgWrap(f.icon) }} />
                        <span className="intake-reason-text">
                          <span className="intake-reason-title">{f.title}</span>
                          <span className="intake-reason-sub">{f.sub}</span>
                        </span>
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <div className="intake-formview">
                  <button type="button" className="intake-back" onClick={() => setFormKey(null)}>
                    <Chevron /> Change reason
                  </button>
                  <div className="intake-form-head">
                    <span className="intake-tile" dangerouslySetInnerHTML={{ __html: svgWrap(form.icon) }} />
                    <div>
                      <h3 className="intake-form-title">{form.title}</h3>
                      <div className="intake-form-est">{form.est} · your answers stay private</div>
                    </div>
                  </div>
                  <div className="intake-panel">
                    {form.fields.map((f, i) => {
                      if (f.t === "note") {
                        return (
                          <div className="intake-field" key={i}>
                            <div className="intake-callout">
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M12 16v-4M12 8h.01" /></svg>
                              <span>{f.msg}</span>
                            </div>
                          </div>
                        );
                      }
                      const v = answers[f.q];
                      return (
                        <div className="intake-field" key={i}>
                          <p className="intake-q">
                            {f.q}
                            {f.t === "multi" && <span className="intake-opt-note">Choose all that apply</span>}
                          </p>
                          {(f.t === "multi" || f.t === "single") && (
                            <div className="intake-chips">
                              {f.opts.map((opt) => {
                                const on = f.t === "single" ? v === opt : Array.isArray(v) && v.includes(opt);
                                return (
                                  <button
                                    key={opt}
                                    type="button"
                                    className={`intake-chip ${on ? "on" : ""}`}
                                    aria-pressed={on}
                                    onClick={() => (f.t === "single" ? setSingle(f.q, opt) : toggleMulti(f.q, opt))}
                                  >
                                    {opt}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                          {f.t === "text" && (
                            <input
                              className="intake-input"
                              placeholder={f.ph}
                              value={(v as string) ?? ""}
                              onChange={(e) => setTextAns(f.q, e.target.value)}
                            />
                          )}
                          {f.t === "area" && (
                            <textarea
                              className="intake-input intake-area"
                              placeholder={f.ph}
                              value={(v as string) ?? ""}
                              onChange={(e) => setTextAns(f.q, e.target.value)}
                            />
                          )}
                          {f.t === "single" && f.flag && v === "Yes" && (
                            <div className="intake-flag">
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" /></svg>
                              <span>Thanks for flagging this — your provider will see it right away. If this feels like an emergency, please call <a href="tel:911">911</a> instead of waiting.</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className="intake-privacy">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
                    Private &amp; shared only with your care team
                  </div>
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="book-stage active">
              <div className="doctor-pick-grid">
                {docList.map((d) => (
                  <button
                    key={d.id}
                    className={`doctor-pick ${doctorId === d.id ? "selected" : ""} ${d.is_favorite ? "is-fav" : ""}`}
                    onClick={() => setCardDoctor(d)}
                  >
                    <Avatar className="doctor-pick-avatar" src={d.avatar_url} initials={`${d.first_name.charAt(0)}${d.last_name.charAt(0)}`} />
                    <span className="doctor-pick-info">
                      <span className="doctor-pick-name">{doctorName(d)}</span>
                      {d.specialty && <span className="doctor-pick-spec">{d.specialty}</span>}
                    </span>
                    {d.is_favorite && (
                      <span className="doctor-pick-fav" aria-label="Favorited">
                        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" /></svg>
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="book-stage active">
              {doctorId && <SlotPicker doctorId={doctorId} value={slot} onChange={setSlot} />}
            </div>
          )}

          {step === 4 && (
            <div className="book-stage active">
              <div className="book-confirm-card">
                <div className="book-summary-row"><span className="lbl">Category</span><span className="val">{CATEGORY_LABEL[form?.category ?? category]}</span></div>
                <div className="book-summary-row"><span className="lbl">Reason</span><span className="val">{form?.title ?? "—"}</span></div>
                <div className="book-summary-row"><span className="lbl">Doctor</span><span className="val">{doctor ? doctorName(doctor) : "—"}</span></div>
                <div className="book-summary-row"><span className="lbl">Time</span><span className="val">{slot ? formatDateTime(slot) : "—"}</span></div>
                <div className="book-summary-row"><span className="lbl">Visit type</span><span className="val">Video telehealth</span></div>
              </div>
              {err && <ErrorState message={err} />}
            </div>
          )}

          <div className="book-actions">
            {step > 1 && (
              <button className="btn btn-ghost" onClick={() => setStep(step - 1)} disabled={busy}>Back</button>
            )}
            {step < 4 ? (
              <button className="btn btn-primary" onClick={() => setStep(step + 1)} disabled={!canContinue}>
                Continue <Arrow />
              </button>
            ) : (
              <button className="btn btn-primary" onClick={submit} disabled={busy || !slot || !doctorId}>
                {busy ? "Booking…" : "Confirm booking"}
              </button>
            )}
          </div>
        </div>

        {step !== 4 && (
          <div className="book-summary">
            <h4>Your visit</h4>
            <div className="book-summary-row"><span className="lbl">Category</span><span className="val">{CATEGORY_LABEL[form?.category ?? category]}</span></div>
            <div className="book-summary-row"><span className="lbl">Reason</span><span className={`val ${form ? "" : "empty"}`}>{form?.title ?? "Not selected"}</span></div>
            <div className="book-summary-row"><span className="lbl">Doctor</span><span className={`val ${doctor ? "" : "empty"}`}>{doctor ? doctorName(doctor) : "Not selected"}</span></div>
            <div className="book-summary-row"><span className="lbl">Time</span><span className={`val ${slot ? "" : "empty"}`}>{slot ? formatDateTime(slot) : "Not selected"}</span></div>
          </div>
        )}
      </div>

      {/* Step-2 doctor profile card. Favoriting re-sorts the list (favorites to top); "Choose"
          selects the doctor and advances to the time step. */}
      {cardDoctor && (
        <Modal open onClose={() => setCardDoctor(null)} wide>
          <DoctorCard
            doctor={cardDoctor}
            onFavoriteChange={(next) => {
              setCardDoctor((prev) => (prev ? { ...prev, is_favorite: next } : prev));
              doctors.reload();
            }}
            footer={
              <>
                <button
                  className="btn btn-primary"
                  style={{ width: "100%" }}
                  onClick={() => {
                    setDoctorId(cardDoctor.id);
                    setSlot(null);
                    setCardDoctor(null);
                    setStep(3);
                  }}
                >
                  Choose {doctorName(cardDoctor)}
                </button>
                <button className="btn btn-ghost" style={{ width: "100%" }} onClick={() => setCardDoctor(null)}>
                  Back to list
                </button>
              </>
            }
          />
        </Modal>
      )}
    </>
  );
}
